/**
 * The advisor behind a reusable invite link (`/onboard/:advisorId`), read from their public profile.
 *
 * Two readers share one React Query entry, keyed by advisor:
 * - AdvisorPublicInvite shows the advisor (`useAdvisorPublicProfile`).
 * - useSupportChat decides whether chat may open on that page (`useAdvisorPublicProfileQuery`),
 *   from `supportChatWithheld`. It is mounted app-wide, so the query only runs on that page.
 *
 * The page's error messages are the ones it showed before this moved to React Query: a missing
 * advisor id, an advisor the server does not know (404), any other failed answer, and no answer.
 */
import { useQuery } from '@tanstack/react-query';
import {
  AdvisorPublicProfileError,
  publicInviteApi,
  type AdvisorPublicProfile,
} from '@client/api/publicInviteApi';

export function useAdvisorPublicProfileQuery(advisorId: string | undefined) {
  return useQuery({
    queryKey: ['advisorPublicProfile', advisorId],
    queryFn: () => publicInviteApi.getAdvisorPublicProfile(advisorId!),
    enabled: !!advisorId,
    retry: false,
  });
}

function profileErrorMessage(error: unknown): string {
  if (error instanceof AdvisorPublicProfileError && error.status === 404) {
    return 'This advisor could not be found';
  }
  if (error instanceof AdvisorPublicProfileError && error.status > 0) {
    return 'Unable to load advisor information';
  }
  return 'Unable to load advisor information. Please try again.';
}

export function useAdvisorPublicProfile(advisorId: string | undefined): {
  advisor: AdvisorPublicProfile | null;
  isLoading: boolean;
  errorMessage: string | null;
} {
  const query = useAdvisorPublicProfileQuery(advisorId);

  if (!advisorId) {
    return { advisor: null, isLoading: false, errorMessage: 'Invalid invitation link' };
  }
  return {
    advisor: query.data ?? null,
    isLoading: query.isPending,
    errorMessage: query.isError && !query.data ? profileErrorMessage(query.error) : null,
  };
}
